import { LoaderView } from '@renderforest/webwood-engine'
import {
  LOADER_BACKGROUND_IMAGE,
  LOADER_SHINE_IMAGE,
  LOADER_GRADIENT_IMAGE,
  LOADER_TEXT_BACKGROUND_IMAGE
} from './consts'
import { logError } from 'utils/logger'

export class WebwoodMediaItemLoader extends LoaderView {
  declare protected rotatableContentContainer: Phaser.GameObjects.Container
  declare protected backgroundImage: Phaser.GameObjects.Image
  declare protected shineImage: Phaser.GameObjects.Image | null
  declare protected loaderMaskGraphics: Phaser.GameObjects.Graphics
  protected shineAnimationSpeed: number = 10
  protected gradientRotationSpeed: number = 2
  declare protected gradientImage: Phaser.GameObjects.Image
  declare protected textBackground: Phaser.GameObjects.Image
  declare protected progressText: Phaser.GameObjects.Text

  public setFlipX(flipX: boolean): void {
    this.backgroundImage.setFlipX(flipX)
  }

  public setFlipY(flipY: boolean): void {
    this.backgroundImage.setFlipY(flipY)
  }

  protected createComponents(): void {
    this.createRotatableContentContainer()
    this.createBackgroundImage()
    this.createMask()
    this.createShineImage()
    this.createGradientImage()
    this.createTextBackground()
    this.createProgressText()
    this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.update, this)
    this.loadAndUpdateBackgroundTextureIfNeeded()
  }

  protected createRotatableContentContainer(): void {
    this.rotatableContentContainer = this.scene.add.container(0, 0)
    this.add(this.rotatableContentContainer)
  }

  protected createBackgroundImage(): void {
    this.backgroundImage = this.scene.add.image(
      0,
      0,
      LOADER_BACKGROUND_IMAGE.key
    )

    this.rotatableContentContainer.add(this.backgroundImage)
    this.updateBackground()
  }

  /**
   * The loader's background image is a placeholder (e.g. the previous asset
   * during regeneration), which can differ from the final asset that will be
   * loaded under `this.config.assetId`. `TextureLoadManager.loadImage` dedupes
   * by key, so reusing `assetId` here would cache the placeholder under the
   * final asset's key and cause the post-generation Image object to skip its
   * own load and keep rendering the stale placeholder. Use a loader-scoped
   * key so the loader only pollutes its own namespace.
   */
  protected get backgroundTextureKey(): string {
    return `${this.config.assetId}-loader-bg`
  }

  protected async loadAndUpdateBackgroundTextureIfNeeded(): Promise<void> {
    const textureKey = this.backgroundTextureKey
    const doesBackgroundImageExist = this.scene.textures.exists(textureKey)
    if (!doesBackgroundImageExist && this.config.metadata.src) {
      await this.scene.textureLoadManager.loadImage(
        this.config.metadata.src,
        textureKey,
        logError
      )
      this.backgroundImage.setTexture(textureKey)
      this.updateBackground()
    }
  }

  protected createShineImage(): void {
    if (!this.backgroundImage) return
    this.shineImage = this.scene.add.image(0, 0, LOADER_SHINE_IMAGE.key)
    this.shineImage.setDisplaySize(
      this.shineImage.width,
      this.shineImage.height
    )

    if (this.config.metadata.src) {
      this.shineImage.setAlpha(0.5)
    }

    this.rotatableContentContainer.add(this.shineImage)
  }

  protected createMask(): void {
    if (!this.backgroundImage) return
    this.loaderMaskGraphics = this.scene.make.graphics()
    this.setMask(
      new Phaser.Display.Masks.GeometryMask(this.scene, this.loaderMaskGraphics)
    )
  }

  protected createGradientImage(): void {
    if (!this.backgroundImage) return
    this.gradientImage = this.scene.add.image(
      this.backgroundImage.x,
      this.backgroundImage.y,
      LOADER_GRADIENT_IMAGE.key
    )
    this.gradientImage.setScale(0.5)
    this.add(this.gradientImage)
  }

  protected createTextBackground(): void {
    if (!this.gradientImage) return
    this.textBackground = this.scene.add.image(
      this.gradientImage.x,
      this.gradientImage.y,
      LOADER_TEXT_BACKGROUND_IMAGE.key
    )
    this.add(this.textBackground)
  }

  protected createProgressText(): void {
    if (!this.textBackground) return
    this.progressText = this.scene.add.text(
      this.textBackground.x,
      this.textBackground.y,
      this.progressTextValue,
      { fontSize: '24px', color: '#000000', fontFamily: 'Montserrat' }
    )
    this.progressText.setOrigin(0.5, 0.5)
    this.progressText.setScale(0.5)
    this.add(this.progressText)
  }

  public update(): void {
    this.updateRotatableContentContainer()
    this.updateMask()
    this.updateBackground()
    this.updateShine()
    this.updateGradient()
    this.updateTextBackground()
    this.updateProgressText()
  }

  protected updateMask(): void {
    if (!this.loaderMaskGraphics || !this.backgroundImage) return

    const width = this.config.width * this.scaleFactor
    const height = this.config.height * this.scaleFactor

    this.loaderMaskGraphics.clear()
    this.loaderMaskGraphics.fillStyle(0xffffff, 1)
    // Draw the rect in the graphics' local space, anchored to the same origin
    // point that backgroundImage uses. The graphics object's own world
    // transform (set below) then handles position + rotation, so the mask
    // tracks the background for any angle, not only multiples of 90deg.
    this.loaderMaskGraphics.fillRect(
      -width * this.config.originX,
      -height * this.config.originY,
      width,
      height
    )

    const matrix = this.backgroundImage.getWorldTransformMatrix()
    const decomposed = matrix.decomposeMatrix()
    this.loaderMaskGraphics.setPosition(
      decomposed.translateX,
      decomposed.translateY
    )
    this.loaderMaskGraphics.setRotation(decomposed.rotation)
  }

  protected updateRotatableContentContainer(): void {
    if (!this.rotatableContentContainer) return
    this.rotatableContentContainer.setPosition(
      this.backgroundImage.displayWidth * this.config.originX,
      this.backgroundImage.displayHeight * this.config.originY
    )
    this.rotatableContentContainer.angle = this.config.angle
  }

  protected updateShine(): void {
    if (this.shineImage) {
      this.shineImage.setDisplaySize(
        this.shineImage.width * this.scaleFactor,
        this.backgroundImage.displayHeight
      )
      if (this.shineImage.x <= this.shineImageEndPosition) {
        this.shineImage.x += this.shineAnimationSpeed
      } else {
        this.shineImage.x = this.shineImageStartPosition
      }
      this.shineImage.y = this.backgroundImage.y
    }
  }

  protected updateBackground(): void {
    if (!this.backgroundImage) {
      return
    }
    this.backgroundImage.setOrigin(this.config.originX, this.config.originY)
    this.backgroundImage.setDisplaySize(
      this.config.width * this.scaleFactor,
      this.config.height * this.scaleFactor
    )
    this.backgroundImage.setDepth(-1)
    // this.backgroundImage.setPosition(
    //   this.backgroundImage.displayWidth * this.config.originX,
    //   this.backgroundImage.displayHeight * this.config.originY
    // )
  }

  protected updateGradient(): void {
    if (!this.gradientImage || !this.backgroundImage) return
    this.gradientImage.x = this.rotatableContentContainer.x
    this.gradientImage.y = this.rotatableContentContainer.y
    this.gradientImage.angle += this.gradientRotationSpeed
  }

  protected updateTextBackground(): void {
    if (!this.textBackground || !this.gradientImage) return
    this.textBackground.x = this.gradientImage.x
    this.textBackground.y = this.gradientImage.y
  }

  protected updateProgressText(): void {
    if (!this.progressText || !this.textBackground) return
    this.progress = this.config.metadata.progress || 0
    this.progressText.x = this.textBackground.x
    this.progressText.y = this.textBackground.y
    this.progressText.setText(this.progressTextValue)
  }

  protected get shineImageStartPosition(): number {
    if (!this.shineImage) return 0
    return this.shineImage ? -this.shineImage.displayWidth / 2 : 0
  }

  protected get shineImageEndPosition(): number {
    if (!this.shineImage || !this.backgroundImage) return 0
    return this.shineImage
      ? this.backgroundImage.displayWidth + this.shineImage.displayWidth / 2
      : 0
  }

  protected get progressTextValue(): string {
    return `${Math.round(this.progress)}%`
  }

  protected get scaleFactor(): number {
    return this.scene.projectMediaManager.scaleFactor
  }

  public destroy(fromScene?: boolean): void {
    this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.update, this)
    this.backgroundImage?.destroy()
    this.shineImage?.destroy()
    this.gradientImage?.destroy()
    this.textBackground?.destroy()
    this.progressText?.destroy()
    const textureKey = this.backgroundTextureKey
    if (this.scene?.textures?.exists(textureKey)) {
      this.scene.textures.remove(textureKey)
    }
    super.destroy(fromScene)
  }
}
